import React from 'react';
import { IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { mergeBlock } from '@core/utils/mergeBlock';
import {
	formatDynamicTextValue,
	parseDynamicTextHelperExpression,
	resolveMergeTagValue,
	t,
} from '@core/utils';
import { BasicBlock } from '@core/components/BasicBlock';
import { get } from 'lodash';

const HERO_VIGNETTE_CLASS_PREFIX = 'hero-vignette';

function sanitizeClassSuffix(value: string) {
	return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function escapeCssUrl(value: string) {
	return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '');
}

function getVignetteGradient(mode: 'dark' | 'light') {
	if (mode === 'light') {
		return 'radial-gradient(circle at 50% 45%, rgba(255,255,255,0.24) 18%, rgba(255,255,255,0.1) 55%, rgba(255,255,255,0.34) 100%)';
	}

	return 'radial-gradient(circle at 50% 45%, rgba(0,0,0,0.02) 25%, rgba(0,0,0,0.28) 62%, rgba(0,0,0,0.58) 100%)';
}

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
const IF_MERGE_TAG_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g;

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
) {
	if (!template || !dataSource) return template || '';

	const getFormattedResolvedValue = (
		resolvedValue: unknown,
		resolvedFormat?: string,
		resolvedMaxLength?: number | null,
	): string | null => {
		if (
			typeof resolvedValue === 'string' ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return formatDynamicTextValue(resolvedValue, resolvedFormat, resolvedMaxLength);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					typeof item === 'string' ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return formatDynamicTextValue(firstValue, resolvedFormat, resolvedMaxLength);
			}
		}

		return null;
	};

	const withConditionalsResolved = template.replace(
		IF_MERGE_TAG_PATTERN,
		(_, conditionKey, trueExpression, fallbackText) => {
			const fallback = String(fallbackText || '').trim();
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const actualKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!actualKey) return fallback;

			const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}

			return fallback;
		},
	);

	return withConditionalsResolved.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedRawKey = rawKey.trim();

		// Skip orphaned Handlebars block tokens
		if (
			trimmedRawKey.startsWith('#') ||
			trimmedRawKey.startsWith('/') ||
			trimmedRawKey === 'else'
		)
			return '';

		const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
		const pipeIdx = trimmedRawKey.indexOf('|');
		const actualKey = parsedHelper
			? parsedHelper.key
			: pipeIdx >= 0
				? trimmedRawKey.slice(0, pipeIdx).trim()
				: trimmedRawKey;
		const fallbackText =
			!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

		const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
		const formattedValue = getFormattedResolvedValue(
			resolvedValue,
			parsedHelper?.format,
			parsedHelper?.maxLength,
		);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — fall through to the tag's own fallback text,
		// or the raw tag, same as an unresolved one. Never render nothing.
		if (formattedValue !== null && formattedValue.trim() !== '') {
			return formattedValue;
		}

		if (fallbackText) return fallbackText;
		return match;
	});
}

export type IHero = IBlockData<
	{
		'background-color'?: string;
		'background-height'?: string;
		'background-position'?: string;
		'background-url'?: string;
		'background-width'?: string;
		'vertical-align'?: string;
		'border-radius'?: string;
		width?: string;
		height?: string;
		mode: 'fluid-height' | 'fixed-height';
		padding?: string;
	},
	{
		overlayMode?: 'none' | 'dark' | 'light';
	}
>;

export const Hero = createBlock<IHero>({
	get name() {
		return t('Hero');
	},
	type: BasicType.HERO,
	create: payload => {
		const defaultData: IHero = {
			type: BasicType.HERO,
			data: {
				value: {
					overlayMode: 'none',
				},
			},
			attributes: {
				'background-color': '#1A365D',
				'background-position': 'center center',
				mode: 'fixed-height',
				padding: '0px 0px 0px 0px',
				'vertical-align': 'middle',
				'background-url':
					'https://bay.cdn.backatyou.com/ui/design/email-editor/BAY_House_V8.png',
				width: 'auto',
				height: '420px',
				'border-radius': '0px',
				'background-width': 'auto',
				'background-height': '360px',
			},
			children: [
				{
					type: 'text',
					data: {
						value: {
							content: 'Find Your Dream Home',
						},
					},
					attributes: {
						padding: '110px 30px 12px 30px',
						align: 'center',
						color: '#ffffff',
						'font-family': 'Avenir Next, Helvetica Neue, Arial, sans-serif',
						'font-size': '42px',
						'font-weight': '800',
						'line-height': '1.2',
						'letter-spacing': '0px',
					},
					children: [],
				},
				{
					type: 'text',
					data: {
						value: {
							content:
								'Discover exclusive local listings, market insights, and expert guidance to help you make your next move with confidence.',
						},
					},
					attributes: {
						align: 'center',
						color: '#ffffff',
						'font-family': 'Helvetica Neue, Arial, sans-serif',
						'font-size': '16px',
						'font-weight': '400',
						padding: '0px 40px 28px 40px',
						'line-height': '1.5',
						'letter-spacing': '0px',
					},
					children: [],
				},
				{
					type: 'button',
					data: {
						value: {
							content: 'Explore the Market',
						},
					},
					attributes: {
						align: 'center',
						'background-color': '#1A365D',
						color: '#FFFFFF',
						'font-family': 'Helvetica Neue, Arial, sans-serif',
						'font-size': '16px',
						'font-weight': '600',
						'border-radius': '4px',
						padding: '0px 25px 70px 25px',
						'inner-padding': '14px 24px 14px 24px',
						'line-height': '120%',
						target: '_blank',
						'vertical-align': 'middle',
						border: 'none',
						'text-align': 'center',
						href: '#',
					},
					children: [],
				},
			],
		};
		return mergeBlock(defaultData, payload);
	},
	validParentType: [BasicType.PAGE, BasicType.WRAPPER],
	render(params) {
		const { data, mode } = params;
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		const overlayMode =
			data.data.value.overlayMode === 'light' || data.data.value.overlayMode === 'dark'
				? data.data.value.overlayMode
				: 'none';

		let renderData = data;
		if (mode === 'testing' && isPreviewMode) {
			const resolvedAttributes = Object.entries(data.attributes || {}).reduce(
				(acc, [key, value]) => {
					const attributeKey = key as keyof IHero['attributes'];
					acc[attributeKey] =
						typeof value === 'string'
							? (resolveDataSourceMergeTags(value, params.dataSource) as any)
							: (value as any);
					return acc;
				},
				{} as IHero['attributes'],
			);

			const resolvedMode =
				resolvedAttributes.mode || data.attributes.mode || 'fixed-height';

			renderData = {
				...data,
				attributes: {
					...resolvedAttributes,
					mode: resolvedMode,
				},
			};
		}

		const existingCssClass = String(renderData.attributes['css-class'] || '').trim();
		const normalizedHeight = String(renderData.attributes.height || '')
			.trim()
			.toLowerCase();
		const resolvedHeroMode =
			normalizedHeight === '' || normalizedHeight === 'auto'
				? 'fluid-height'
				: 'fixed-height';

		const styleTags: string[] = [];

		renderData = {
			...renderData,
			attributes: {
				...renderData.attributes,
				mode: resolvedHeroMode,
				'background-width': 'auto',
				'css-class': existingCssClass,
			},
		};

		const backgroundUrl = String(renderData.attributes['background-url'] || '').trim();
		if (overlayMode !== 'none' && backgroundUrl) {
			const classSuffix = sanitizeClassSuffix(params.idx || 'root');
			const vignetteClass = `${HERO_VIGNETTE_CLASS_PREFIX}-${classSuffix}`;
			const overlayCssClass = [
				renderData.attributes['css-class'],
				HERO_VIGNETTE_CLASS_PREFIX,
				`${HERO_VIGNETTE_CLASS_PREFIX}--${overlayMode}`,
				vignetteClass,
			]
				.filter(Boolean)
				.join(' ');

			styleTags.push(
				`<style>.${vignetteClass} > table > tbody > tr > td { background-image: ${getVignetteGradient(
					overlayMode,
				)}, url("${escapeCssUrl(backgroundUrl)}") !important; background-size: cover !important; background-position: center center !important; background-repeat: no-repeat !important; }</style>`,
			);

			return (
				<>
					{`<mj-raw>${styleTags.join('')}</mj-raw>`}
					<BasicBlock
						params={{
							...params,
							data: {
								...renderData,
								attributes: {
									...renderData.attributes,
									'css-class': overlayCssClass,
								},
							},
						}}
						tag='mj-hero'
					/>
				</>
			);
		}

		if (styleTags.length) {
			return (
				<>
					{`<mj-raw>${styleTags.join('')}</mj-raw>`}
					<BasicBlock
						params={{
							...params,
							data: renderData,
						}}
						tag='mj-hero'
					/>
				</>
			);
		}

		return (
			<BasicBlock
				params={{
					...params,
					data: renderData,
				}}
				tag='mj-hero'
			/>
		);
	},
});
